import React, { useEffect, useState, useCallback } from "react";
import { UserPlus, Mail, Trash2, RotateCw, Crown, Briefcase, CheckCircle2, Clock, Loader2, Copy, LogIn } from "lucide-react";
import api from "../../api";
import { toast } from "sonner";

/**
 * UsersPanel — Phase 1 (super-admin only).
 * Lists/invites/deletes users for a given tenant. Reused inside TenantsPanel
 * (super-admin) and (Phase 2) inside AdminDashboard for tenant-owners.
 *
 * Props:
 *   - tenantSlug (string, required)
 *   - mode: "super" | "owner" (Phase 1: "super" only)
 */
export const UsersPanel = ({ tenantSlug, mode = "super" }) => {
    const basePath = mode === "super"
        ? `/super/tenants/${tenantSlug}/users`
        : `/admin/users`;

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("staff");
    const [submitting, setSubmitting] = useState(false);
    const [lastSetupUrl, setLastSetupUrl] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(basePath);
            setUsers(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            toast.error("No se pudo cargar la lista de usuarios");
        } finally {
            setLoading(false);
        }
    }, [basePath]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const submitInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) return;
        setSubmitting(true);
        setLastSetupUrl(null);
        try {
            const r = await api.post(basePath, {
                email: inviteEmail.trim(),
                role: inviteRole,
            });
            toast.success(`Invitación enviada a ${inviteEmail}`);
            setInviteEmail("");
            if (r.data?.setup_url) {
                setLastSetupUrl(r.data.setup_url);
            }
            await refresh();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "No se pudo enviar la invitación");
        } finally {
            setSubmitting(false);
        }
    };

    const reinvite = async (userId, email) => {
        try {
            const r = await api.post(`${basePath}/${userId}/reinvite`);
            toast.success(`Invitación reenviada a ${email}`);
            if (r.data?.setup_url) {
                setLastSetupUrl(r.data.setup_url);
            }
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo reenviar la invitación");
        }
    };

    const removeUser = async (userId, email) => {
        if (!window.confirm(`¿Eliminar el usuario ${email}? Esta acción es reversible solo desde el administrador.`)) return;
        try {
            await api.delete(`${basePath}/${userId}`);
            toast.success(`Usuario eliminado`);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo eliminar el usuario");
        }
    };

    const impersonate = async (userId, email) => {
        if (mode !== "super") return;
        if (!window.confirm(`Iniciar sesión como ${email}?\n\nTu sesión de super-admin será reemplazada por una sesión de 1 hora como este usuario. Regístrate de nuevo para volver.`)) return;
        try {
            await api.post(`${basePath}/${userId}/impersonate`);
            toast.success(`Ahora estás viendo el panel como ${email}`);
            // Redirect to /admin so the dashboard remounts with the new session
            window.location.href = "/admin";
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo iniciar sesión como este usuario");
        }
    };

    const copyUrl = (url) => {
        navigator.clipboard.writeText(url).then(() => toast.success("Link copiado"));
    };

    return (
        <div data-testid={`users-panel-${tenantSlug}`} className="space-y-4">
            {/* Invite form */}
            <form onSubmit={submitInvite} className="bg-white border border-earth-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-earth-800 font-semibold text-sm">
                    <UserPlus className="w-4 h-4" /> Invitar nuevo usuario / Invite new user
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
                    <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="email@example.com"
                        required
                        data-testid={`users-invite-email-${tenantSlug}`}
                        className="px-3 py-2 rounded-lg border border-earth-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 outline-none text-sm"
                    />
                    <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        data-testid={`users-invite-role-${tenantSlug}`}
                        className="px-3 py-2 rounded-lg border border-earth-300 focus:border-terracotta-500 outline-none text-sm bg-white"
                    >
                        <option value="staff">Staff (orders + menu)</option>
                        <option value="owner">Owner (full)</option>
                    </select>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid={`users-invite-submit-${tenantSlug}`}
                        className="px-4 py-2 rounded-lg bg-terracotta-600 hover:bg-terracotta-700 disabled:bg-earth-400 text-white font-semibold text-sm inline-flex items-center gap-2"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        Invitar
                    </button>
                </div>
                {lastSetupUrl && (
                    <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
                        <div className="font-semibold mb-1">Dev mode — setup link (copy and send manually if email isn&apos;t configured):</div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 truncate font-mono text-[11px]">{lastSetupUrl}</code>
                            <button
                                type="button"
                                onClick={() => copyUrl(lastSetupUrl)}
                                className="p-1.5 rounded hover:bg-amber-200/50"
                                title="Copy"
                            >
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}
            </form>

            {/* List */}
            <div className="bg-white border border-earth-200 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-6 flex items-center gap-3 text-earth-600">
                        <Loader2 className="w-4 h-4 animate-spin" /> Cargando usuarios…
                    </div>
                ) : users.length === 0 ? (
                    <div data-testid={`users-empty-${tenantSlug}`} className="p-8 text-center text-earth-500 text-sm">
                        Aún no hay usuarios. Invita al primero arriba.
                        <div className="text-xs opacity-70 mt-1">No users yet. Invite the first one above.</div>
                    </div>
                ) : (
                    <ul className="divide-y divide-earth-100">
                        {users.map((u) => (
                            <li key={u.id} data-testid={`users-row-${u.id}`} className="p-4 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${u.role === "owner" ? "bg-amber-100 text-amber-700" : "bg-earth-100 text-earth-700"}`}>
                                        {u.role === "owner" ? <Crown className="w-4 h-4" /> : <Briefcase className="w-4 h-4" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-earth-900 truncate">{u.email}</div>
                                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-earth-500 mt-0.5">
                                            <span className="uppercase tracking-wider font-bold">{u.role}</span>
                                            <span>·</span>
                                            {u.status === "invited" ? (
                                                <span className="inline-flex items-center gap-1 text-amber-700">
                                                    <Clock className="w-3 h-3" /> Invitado / Pending
                                                </span>
                                            ) : u.status === "active" ? (
                                                <span className="inline-flex items-center gap-1 text-green-700">
                                                    <CheckCircle2 className="w-3 h-3" /> Activo
                                                </span>
                                            ) : (
                                                <span className="text-red-600">{u.status}</span>
                                            )}
                                            {u.last_login_at && (
                                                <>
                                                    <span>·</span>
                                                    <span>Último acceso {new Date(u.last_login_at).toLocaleDateString()}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 self-end sm:self-center">
                                    {mode === "super" && u.status === "active" && (
                                        <button
                                            type="button"
                                            onClick={() => impersonate(u.id, u.email)}
                                            data-testid={`users-impersonate-${u.id}`}
                                            className="px-3 py-1.5 rounded-lg border border-terracotta-300 hover:bg-terracotta-50 text-terracotta-700 text-xs font-semibold inline-flex items-center gap-1.5"
                                            title="Iniciar sesión como este usuario / Login as this user"
                                        >
                                            <LogIn className="w-3.5 h-3.5" /> Login as
                                        </button>
                                    )}
                                    {(u.status === "invited" || u.status === "active") && (
                                        <button
                                            type="button"
                                            onClick={() => reinvite(u.id, u.email)}
                                            data-testid={`users-reinvite-${u.id}`}
                                            className="px-3 py-1.5 rounded-lg border border-earth-300 hover:bg-earth-50 text-earth-700 text-xs font-semibold inline-flex items-center gap-1.5"
                                            title="Reenviar invitación"
                                        >
                                            <RotateCw className="w-3.5 h-3.5" /> Reenviar
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeUser(u.id, u.email)}
                                        data-testid={`users-delete-${u.id}`}
                                        className="px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-700 text-xs font-semibold inline-flex items-center gap-1.5"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default UsersPanel;
